import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { addCrumbs, getCrumbs, spendCrumbs } from '../core/save.js';
import { QUESTS, questsForGiver, acceptQuest, turnIn, objectiveProgress, flagObjective } from '../core/quests.js';

// Lucky Crumb Casino — slots + memory card game.

export function createCasinoScene(){
  return () => new CasinoScene();
}

const REELS = ['🍞', '🍒', '🍋', '💰', '🥇'];
const PAYTABLE = {
  '🍞🍞🍞': 10,
  '🍒🍒🍒': 15,
  '🍋🍋🍋': 20,
  '💰💰💰': 40,
  '🥇🥇🥇': 80,
  'any2': 2
};
const BET_SIZES = [5, 10, 25];

class CasinoScene {
  constructor(){
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x2A1F3D);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 14;
    this.camTarget = new THREE.Vector3(0, 1.6, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildRoom();

    this.dealer = this._makeDealer();
    this.scene.add(this.dealer.root);

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 4.5);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.hotspots = [
      { pos: this.dealer.root.position.clone(), r: 2.4, label: '🗣 talk to Dealer', action: () => this._talkToDealer() },
      { pos: new THREE.Vector3(-3.5, 0, -1), r: 2.2, label: '🎰 play slots', action: () => this._startSlots() },
      { pos: new THREE.Vector3( 3.5, 0, -1), r: 2.2, label: '🃏 memory cards', action: () => this._startMemory() },
      { pos: new THREE.Vector3(0, 0, 6), r: 2.2, label: '← back to Uptown', action: () => this.manager.goTo('uptown', { from: 'casino' }) }
    ];
    this.nearSpot = null;
    this.panels = [];
    this.state = 'explore';
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xE6D7FF, 0.45));
    const spot = new THREE.DirectionalLight(0xFFD9A5, 0.55);
    spot.position.set(3, 12, 6); spot.castShadow = true;
    spot.shadow.mapSize.set(2048, 2048);
    const s = 16;
    spot.shadow.camera.left = -s; spot.shadow.camera.right = s;
    spot.shadow.camera.top = s; spot.shadow.camera.bottom = -s;
    this.scene.add(spot);
    // colored accent lights
    const p1 = new THREE.PointLight(0x9B59B6, 0.6, 12); p1.position.set(-4, 3, -2); this.scene.add(p1);
    const p2 = new THREE.PointLight(0xF49B33, 0.6, 12); p2.position.set(4, 3, -2); this.scene.add(p2);
  }

  _buildRoom(){
    const floor = new THREE.Mesh(new THREE.PlaneGeometry(18, 16), new THREE.MeshLambertMaterial({ color: 0x3E2F4E }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);
    // tables
    box(2.4, 0.9, 1.2, 0x5A3D8C, -3.5, 0.45, -1, this.scene);
    box(2.4, 0.9, 1.2, 0x5A3D8C,  3.5, 0.45, -1, this.scene);
    // dealer desk
    box(3, 1.0, 1.0, 0x4A3020, 0, 0.5, -5, this.scene);
    const sign = textSprite('Lucky Crumb Casino', 42, '#F7C948', 5.5, 1.3);
    sign.position.set(0, 3.2, -5); this.scene.add(sign);
  }

  _makeDealer(){
    const build = (g) => {
      cyl(.34, .34, .55, 0xF3E2BE, 0, .62, 0, g, 16); // bagel body
      sphere(.30, 0xE9C46A, 0, 1.1, 0, g, 16); // head
      box(.20, .06, .08, 0x2E2018, 0, .98, .28, g); // smile
      box(.05, .05, .05, 0x2E2018, -.10, 1.12, .24, g);
      box(.05, .05, .05, 0x2E2018,  .10, 1.12, .24, g);
      cyl(.03, .03, .34, C.cream, -.42, .72, .12, g, 6);
      cyl(.03, .03, .34, C.cream,  .42, .72, .12, g, 6);
      cyl(.06, .06, .40, 0x3D5FC0, -.12, .20, 0, g, 6);
      cyl(.06, .06, .40, 0x3D5FC0,  .12, .20, 0, g, 6);
      const hat = cyl(.34, .34, .10, 0x1A1A2E, 0, 1.42, 0, g, 16);
      box(.10, .26, .10, 0x1A1A2E, 0, 1.62, 0, g);
    };
    return new Townsperson({
      name: 'Dealer Dough', line: 'feeling lucky, slice?',
      pos: new THREE.Vector3(0, 0, -3.5), facing: 0, wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  _talkToDealer(){
    this.bread.stop();
    this.bread.drawFace('happy');
    this.dealerTab = 'quests';
    this._renderDealerDialog();
  }

  _renderDealerDialog(){
    this._clearPanels();
    const q = questsForGiver('dealer');
    const activeRows = q.active.map(id => this._questRow(id)).join('');
    const availRows = q.available.map(id => this._questRow(id, true)).join('');
    const body = activeRows || availRows || '<p class="cafe-sub">No jobs on the board. Try your luck at the tables!</p>';
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Dealer Dough</h2>
        <div class="cafe-sub">Lucky Crumb Casino</div>
        <div class="q-tabs">
          <button data-tab="quests" class="cafe-btn ghost ${this.dealerTab === 'quests' ? 'active' : ''}">quests</button>
          <button data-tab="games" class="cafe-btn ghost ${this.dealerTab === 'games' ? 'active' : ''}">games</button>
        </div>
        <div class="q-tab-body">${this.dealerTab === 'quests' ? body : '<p class="cafe-sub">Slots and Memory Cards are open. Play responsibly!</p>'}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>
    `);
    p.querySelector('[data-tab="quests"]').onclick = () => { this.dealerTab = 'quests'; this._renderDealerDialog(); };
    p.querySelector('[data-tab="games"]').onclick = () => { this.dealerTab = 'games'; this._renderDealerDialog(); };
    p.querySelector('.close').onclick = () => this._clearPanels();
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this._renderDealerDialog(); });
    p.querySelectorAll('.turnin').forEach(b => {
      b.onclick = () => { turnIn(b.dataset.id); HUD.refreshCrumbs(); HUD.refreshQuests(); this._renderDealerDialog(); };
    });
  }

  _questRow(id, accept = false){
    const q = QUESTS[id];
    if (accept) return `<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}</span></div><button class="cafe-btn accept" data-id="${id}">accept</button></div>`;
    const { have, goal, ready } = objectiveProgress(id);
    return `<div class="q-drow ${ready ? 'ready' : ''}"><div><b>${q.title}</b><span>${have}/${goal} ${q.objective.noun}</span></div>${ready ? `<button class="cafe-btn turnin" data-id="${id}">turn in</button>` : ''}</div>`;
  }

  // ---- slots ----
  _startSlots(){
    this._clearPanels();
    this.state = 'playing';
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');
    this.bread.stop();
    this.bread.root.position.set(-3.5, 0, 1.2);
    this.bread.root.rotation.y = Math.PI;
    this.slotBet = 5;
    this._buildSlotsPanel();
  }

  _buildSlotsPanel(){
    this._clearPanels();
    const p = this._panel(`
      <div class="cafe-card">
        <h2>Lucky Crumb Slots</h2>
        <div class="cafe-sub">Wallet: 🍞 ${getCrumbs()}</div>
        <div class="slot-reels" id="slot-reels">
          <div class="slot-reel">?</div>
          <div class="slot-reel">?</div>
          <div class="slot-reel">?</div>
        </div>
        <div class="slot-bet" id="slot-bet">
          ${BET_SIZES.map(b => `<button class="cafe-btn ${b === this.slotBet ? 'active' : ''}" data-bet="${b}">🍞 ${b}</button>`).join('')}
        </div>
        <button class="cafe-btn" id="slot-spin">spin</button>
        <div class="cafe-payout" id="slot-result"></div>
        <button class="cafe-quit" id="slot-quit">✕ leave</button>
      </div>
    `);
    this.reelEls = Array.from(p.querySelectorAll('.slot-reel'));
    p.querySelectorAll('#slot-bet button').forEach(b => b.onclick = () => { this.slotBet = +b.dataset.bet; this._buildSlotsPanel(); });
    p.querySelector('#slot-spin').onclick = () => this._spinSlots();
    p.querySelector('#slot-quit').onclick = () => this._teardownGame();
  }

  async _spinSlots(){
    if (!spendCrumbs(this.slotBet)){
      document.getElementById('slot-result').textContent = 'not enough crumbs!';
      return;
    }
    flagObjective('casino-try', 'playedCasino');
    HUD.refreshCrumbs();
    document.getElementById('slot-result').textContent = '';
    const spinBtn = document.getElementById('slot-spin');
    if (spinBtn) spinBtn.disabled = true;

    // animate
    const final = [REELS[(Math.random() * REELS.length) | 0], REELS[(Math.random() * REELS.length) | 0], REELS[(Math.random() * REELS.length) | 0]];
    for (let i = 0; i < 3; i++){
      await new Promise(r => setTimeout(r, 350));
      // show random flurry
      let steps = 8;
      const interval = setInterval(() => {
        this.reelEls[i].textContent = REELS[(Math.random() * REELS.length) | 0];
        steps--;
        if (steps <= 0){
          clearInterval(interval);
          this.reelEls[i].textContent = final[i];
        }
      }, 60);
      await new Promise(r => setTimeout(r, 550));
    }

    const key = final.join('');
    let payout = PAYTABLE[key] || 0;
    if (payout === 0){
      // any two matching
      if (final[0] === final[1] || final[1] === final[2] || final[0] === final[2]) payout = PAYTABLE.any2;
    }
    const win = payout * this.slotBet;
    if (win > 0){
      addCrumbs(win);
      HUD.refreshCrumbs();
    }
    const res = document.getElementById('slot-result');
    if (res) res.textContent = payout ? `You win 🍞 ${win}!` : 'No match. Try again!';
    if (spinBtn) spinBtn.disabled = false;
  }

  // ---- memory ----
  _startMemory(){
    this._clearPanels();
    this.state = 'playing';
    this.input.onTap = null;
    HUD.showEnter(null); HUD.setHint(''); HUD.hideHint(); HUD.say('');
    this.bread.stop();
    this.bread.root.position.set(3.5, 0, 1.2);
    this.bread.root.rotation.y = Math.PI;
    this._buildMemoryPanel();
  }

  _buildMemoryPanel(){
    this._clearPanels();
    const icons = ['🍞','🍒','🍋','💰','🥇','🎰','🃏','🎲'];
    const deck = [...icons, ...icons].sort(() => Math.random() - 0.5);
    this.memoryDeck = deck;
    this.memoryFlipped = [];
    this.memoryMatched = new Set();
    this.memoryMoves = 0;
    this.memoryStart = performance.now();

    const p = this._panel(`
      <div class="cafe-card">
        <h2>Card Flip</h2>
        <div class="cafe-sub">Match all the pairs. Fewer moves = bigger payout.</div>
        <div class="memory-grid" id="memory-grid">
          ${deck.map((_, i) => `<div class="memory-card" data-idx="${i}">?</div>`).join('')}
        </div>
        <div class="cafe-payout" id="memory-result"></div>
        <button class="cafe-quit" id="memory-quit">✕ leave</button>
      </div>
    `);
    p.querySelectorAll('.memory-card').forEach(c => c.onclick = () => this._flipCard(+c.dataset.idx));
    p.querySelector('#memory-quit').onclick = () => this._teardownGame();
  }

  _flipCard(idx){
    if (this.memoryFlipped.length >= 2 || this.memoryFlipped.includes(idx) || this.memoryMatched.has(idx)) return;
    const card = document.querySelector(`.memory-card[data-idx="${idx}"]`);
    if (!card) return;
    card.textContent = this.memoryDeck[idx];
    card.classList.add('flipped');
    this.memoryFlipped.push(idx);

    if (this.memoryFlipped.length === 2){
      this.memoryMoves++;
      const [a, b] = this.memoryFlipped;
      if (this.memoryDeck[a] === this.memoryDeck[b]){
        setTimeout(() => {
          document.querySelector(`.memory-card[data-idx="${a}"]`)?.classList.add('matched');
          document.querySelector(`.memory-card[data-idx="${b}"]`)?.classList.add('matched');
          this.memoryMatched.add(a); this.memoryMatched.add(b);
          this.memoryFlipped = [];
          if (this.memoryMatched.size === this.memoryDeck.length) this._finishMemory();
        }, 400);
      } else {
        setTimeout(() => {
          document.querySelector(`.memory-card[data-idx="${a}"]`)?.classList.remove('flipped');
          document.querySelector(`.memory-card[data-idx="${b}"]`)?.classList.remove('flipped');
          document.querySelector(`.memory-card[data-idx="${a}"]`).textContent = '?';
          document.querySelector(`.memory-card[data-idx="${b}"]`).textContent = '?';
          this.memoryFlipped = [];
        }, 700);
      }
    }
  }

  _finishMemory(){
    flagObjective('casino-try', 'playedCasino');
    const time = (performance.now() - this.memoryStart) / 1000;
    const maxPairs = this.memoryDeck.length / 2;
    // ideal moves = maxPairs; payout scales down
    const accuracy = Math.max(0, (maxPairs * 2 - this.memoryMoves) / maxPairs);
    const crumbs = Math.round(Math.max(5, 40 - this.memoryMoves * 2 - time * 0.5));
    addCrumbs(crumbs);
    HUD.refreshCrumbs();
    const res = document.getElementById('memory-result');
    if (res) res.textContent = `Matched! +${crumbs} crumbs · ${this.memoryMoves} moves · ${time.toFixed(1)}s`;
  }

  _teardownGame(){
    this._clearPanels();
    this.state = 'explore';
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('casino');
    HUD.say('Back on the casino floor.');
  }

  _tap(ray){
    if (this.state !== 'explore') return;
    if (checkNpcClick(ray, this.dealer, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-8, Math.min(8, gp.x));
    gp.z = Math.max(-6, Math.min(6, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this.state = 'explore';
    HUD.setSubtitle('Lucky Crumb Casino');
    HUD.say('The casino glows with neon crumbs. Try the slots or card tables.');
    HUD.setHint('click to walk · play slots/cards or talk to Dealer Dough');
    HUD.hideHintSoon(7000);
  }

  exit(){
    this.input.onTap = null;
    HUD.showEnter(null);
    HUD.say('');
    this._clearPanels();
  }

  update(dt){
    this.bread.update(dt);
    this.dealer.update(dt, this.bread.root.position);

    if (this.state === 'explore'){
      let near = null, nd = Infinity;
      this.hotspots.forEach(h => {
        const d = h.pos.distanceTo(this.bread.root.position);
        if (d < nd){ nd = d; near = h; }
      });
      if (near && nd < near.r){
        const key = near.label;
        if (this.nearSpot !== key){
          this.nearSpot = key;
          HUD.showEnter(near.label, () => near.action());
        }
      } else if (this.nearSpot){
        this.nearSpot = null;
        HUD.showEnter(null);
      }
    }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5 + 1), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
